import React, {useState} from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {Button, DialogActions, DialogContent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import AlertDialog from "../alert-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import MaterialTable from "../material-table";
import SettingsDialog from "../settings-dialog";
import AiSearchEngineSettings from "./AiSearchEngineSettings";
import {
  deleteSearchEngine,
  hideSearchEngineListModal,
  showSearchEngineSettingsModal
} from "../../state/ai-assistant/aiAssistantAction";
import {aiNodeSettingsSelector} from "../../state/ai-assistant/aiAssistantSelector";
import {languageSelector, languagesSelector} from "../../state/app/appSelectors";
import {externalServicesSelector} from "../../state/config/configSelectors";
import {localizeI18nObj} from "../../utils/i18n";

const AiSearchEnginesDialog = ({searchEngines, settings}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const languages = useSelector(languagesSelector);
  const language = useSelector(languageSelector);
  const baseUrl = useSelector(externalServicesSelector)?.ai;
  const {isSearchEngineListVisible, nodeId, authToken} = useSelector(aiNodeSettingsSelector);

  const [alert, setAlert] = useState({open: false, message: "", hasSubmit: false, engineId: null});

  const handleDeleteSearchEngine = id => {
    const entries = Object.values(settings).filter(setting => setting.engineId === id);
    if (entries.length === 0) {
      setAlert({
        open: true,
        message: t("modules.aiAssistant.aiSettings.searchEngineList.deleteSearchEngine.noProperty"),
        hasSubmit: true,
        engineId: id
      });
    } else if (entries.some(item => item.enabled)) {
      setAlert({
        open: true,
        message: t("modules.aiAssistant.aiSettings.searchEngineList.deleteSearchEngine.activeProperty"),
        hasSubmit: false,
        engineId: id
      });
    } else {
      setAlert({
        open: true,
        message: t("modules.aiAssistant.aiSettings.searchEngineList.deleteSearchEngine.noActiveProperty"),
        hasSubmit: true,
        engineId: id
      });
    }
  };

  const handleAlertClose = () => {
    setAlert({open: false, message: "", hasSubmit: false, engineId: null});
  };

  return (
    <SettingsDialog
      title={t("modules.aiAssistant.aiSettings.searchEngineList.dialog.title")}
      open={isSearchEngineListVisible}
      onClose={() => dispatch(hideSearchEngineListModal())}
    >
      <MaterialTable
        id="search-engine-list-table"
        data={searchEngines || []}
        rightActions={[
          {
            id: "search-engine-list__create",
            label: t("modules.aiAssistant.aiSettings.searchEngineList.actions.create"),
            startIcon: <AddIcon />,
            onClick: () => dispatch(showSearchEngineSettingsModal(null))
          }
        ]}
        actions={[
          {
            icon: <EditIcon />,
            tooltip: t("modules.aiAssistant.aiSettings.searchEngineList.actions.edit"),
            onClick: (_, {id}) => dispatch(showSearchEngineSettingsModal(id))
          },
          {
            icon: <DeleteIcon />,
            tooltip: t("modules.aiAssistant.aiSettings.searchEngineList.actions.delete"),
            onClick: (_, {id}) => handleDeleteSearchEngine(id)
          }
        ]}
        columns={[
          {
            hidden: true,
            field: "id",
            title: t("modules.aiAssistant.aiSettings.searchEngineList.columns.id"),
            getClassName: () => "search-engine-list__columns__id"
          },
          {
            field: "label",
            title: t("modules.aiAssistant.aiSettings.searchEngineList.columns.label"),
            render: ({label}) => localizeI18nObj(label, language, languages),
            getClassName: () => "search-engine-list__columns__label"
          },
          {
            field: "type",
            title: t("modules.aiAssistant.aiSettings.searchEngineList.columns.type"),
            getClassName: () => "search-engine-list__columns__type"
          },
          {
            field: "model",
            title: t("modules.aiAssistant.aiSettings.searchEngineList.columns.model"),
            getClassName: () => "search-engine-list__columns__model"
          }
        ]}
      />

      <AiSearchEngineSettings />

      <AlertDialog open={alert.open} onClose={handleAlertClose}>
        <CustomDialogTitle onClose={handleAlertClose}>
          {t("modules.aiAssistant.aiSettings.searchEngineList.deleteSearchEngine.alert.title")}
        </CustomDialogTitle>
        <DialogContent>{alert.message}</DialogContent>
        <DialogActions>
          {alert.hasSubmit ? (
            <>
              <Button id="confirmation-dialog__cancel-button" autoFocus onClick={handleAlertClose}>
                {t("commons.confirm.cancel")}
              </Button>
              <Button
                id="confirmation-dialog__cancel-button"
                autoFocus
                onClick={() => dispatch(deleteSearchEngine(nodeId, alert.engineId, baseUrl, authToken))}
              >
                {t("commons.confirm.delete")}
              </Button>
            </>
          ) : (
            <Button id="confirmation-dialog__cancel-button" autoFocus onClick={handleAlertClose}>
              {t("commons.confirm.close")}
            </Button>
          )}
        </DialogActions>
      </AlertDialog>
    </SettingsDialog>
  );
};

export default AiSearchEnginesDialog;
